import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Chip,
  List,
  ListItem,
  Divider,
  Snackbar,
} from '@mui/material';
import { useAppSelector } from '../store/hooks';
import { saveQuizResult } from '../services/firestore/quizResultService';
import { recordQuizAttempt, hasAttemptedQuiz } from '../services/quizAttemptService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LoginModal from '../components/auth/LoginModal';

const QuizResultsPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { result } = useAppSelector((state) => state.quiz);
  const { courses } = useAppSelector((state) => state.courses);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [loginModalOpen, setLoginModalOpen] = React.useState(false);

  // Save quiz result and record attempt
  useEffect(() => {
    if (result) {
      const course = courses.find((c) => c.id === courseId);
      
      if (isAuthenticated && user) {
        // Save to Firestore for logged-in users
        saveQuizResult(
          result,
          course?.category,
          'intermediate', // Default difficulty, can be enhanced later
          user.displayName || user.email || undefined,
          user.email || undefined
        ).catch((error) => {
          setSaveError(error.message);
        });
      } else {
        // Record attempt in localStorage for free users
        recordQuizAttempt(result.courseId, result.score);
      }
    }
  }, [result, isAuthenticated, user, courseId, courses]);

  if (!result) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="info">No quiz results found. Please take a quiz first.</Alert>
          <Button onClick={() => navigate(`/courses/${courseId}`)} sx={{ mt: 2 }}>
            Back to Course
          </Button>
        </Box>
      </Container>
    );
  }

  const course = courses.find((c) => c.id === courseId);

  const handleRetakeQuiz = () => {
    if (!isAuthenticated && courseId && hasAttemptedQuiz(courseId)) {
      // Free user has already attempted, show login modal
      setLoginModalOpen(true);
    } else {
      navigate(`/courses/${courseId}/quiz`);
    }
  };

  const handleBackToCourse = () => {
    navigate(`/courses/${courseId}`);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>
          Quiz Results
        </Typography>
        {course && (
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            {course.title}
          </Typography>
        )}

        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" color="primary" gutterBottom>
              {result.score}%
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {result.correctCount} out of {result.totalQuestions} correct
            </Typography>
            <Chip
              label={result.completionStatus === 'completed' ? 'Completed' : 'Incomplete'}
              color={result.completionStatus === 'completed' ? 'success' : 'warning'}
              sx={{ mt: 2 }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Answer Review
          </Typography>
          <List>
            {result.questions.map((q, index) => (
              <React.Fragment key={q.questionId}>
                <ListItem>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {q.isCorrect ? (
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      ) : (
                        <CancelIcon color="error" sx={{ mr: 1 }} />
                      )}
                      <Typography variant="subtitle1" fontWeight="bold">
                        Question {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {q.questionText}
                    </Typography>
                    <Box sx={{ ml: 4 }}>
                      <Typography variant="body2" color={q.isCorrect ? 'success.main' : 'error.main'}>
                        Your answer: {q.userAnswer}
                      </Typography>
                      {!q.isCorrect && (
                        <Typography variant="body2" color="success.main">
                          Correct answer: {q.correctAnswer}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </ListItem>
                {index < result.questions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isAuthenticated && courseId && hasAttemptedQuiz(courseId) ? (
            <Button variant="contained" onClick={() => setLoginModalOpen(true)}>
              Log In to Retake Quiz
            </Button>
          ) : (
            <Button variant="contained" onClick={handleRetakeQuiz}>
              Retake Quiz
            </Button>
          )}
          <Button variant="outlined" onClick={handleBackToCourse}>
            Back to Course
          </Button>
        </Box>

        <Snackbar
          open={!!saveError}
          autoHideDuration={6000}
          onClose={() => setSaveError(null)}
          message={`Failed to save result: ${saveError}`}
        />

        <LoginModal
          open={loginModalOpen}
          onClose={() => setLoginModalOpen(false)}
          onSuccess={() => {
            // User successfully logged in, they can now retake
            if (courseId) {
              navigate(`/courses/${courseId}/quiz`);
            }
          }}
        />
      </Box>
    </Container>
  );
};

export default QuizResultsPage;

